import axios from "axios";
import { DataFormat } from "utils/format/DataFormat";
import { getMessage } from "utils/helpersInTs/helpersInTs";
import { strings } from "utils/localizedStrings";
import ModelConverter from "../../utils/modelConverter";
import { BaseApiResponseModel } from "../baseApiResponseModel/BaseApiResponseModel";
import IApiClient from "./IApiClient";

const serializeParams = (params: Record<string, any>): string => {
  const parts: string[] = [];
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v === undefined || v === null) return;
        parts.push(
          `${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`
        );
      });
    } else {
      parts.push(
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
      );
    }
  });
  return parts.join("&");
};

const api = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL!,
  timeout: 90000,
  paramsSerializer: {
    serialize: serializeParams,
  } as any,
});

// Add a request interceptor
api.interceptors.request.use(
  (config) => {
    const token = DataFormat.getToken();
    const alterToken = DataFormat.getAlterToken();
    const request = config;

    if (token || alterToken) {
      request.headers = {
        ...(request.headers || {}),
        Authorization: `Bearer ${token ? token : alterToken}`,
      } as any;
    }

    //for ngrok
    if (request?.url?.includes("ngrok-free.app")) {
      request.headers!["ngrok-skip-browser-warning"] = "69420";
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Add a response interceptor
api.interceptors.response.use(
  (response) => {
    // Return JSON data
    if (response.data) {
      return response.data;
    }
    return response;
  },
  (error) => {
    const token = DataFormat.getToken();
    const alterToken = DataFormat.getAlterToken();
    const err = error.response || error;
    if (error?.response?.status === 403) {
      if (!!token || !!alterToken) {
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = "/login";
      }
    }
    return Promise.reject(err.data);
  }
);

class AxiosClient implements IApiClient {
  async post<T extends Object>(
    path: string,
    data: Map<string, any> | any = {},
    config?: Map<string, any> | any
  ): Promise<BaseApiResponseModel<T>> {
    let response = await api.post(path, data, config);
    return ModelConverter.decode(response, BaseApiResponseModel<T>);
  }

  async get<T extends Object>(
    path: string,
    data: Map<string, any> | any = {}
  ): Promise<BaseApiResponseModel<T>> {
    let response = await api.get(path, {
      params: data,
    });
    return ModelConverter.decode(response, BaseApiResponseModel<T>);
  }

  async getBlob(
    path: string,
    data: Map<string, any> | any = {},
    config?: Map<string, any> | any
  ): Promise<Blob> {
    const response = await api.get(path, {
      params: data,
      responseType: "blob",
      headers: {
        "Content-Type": "application/xml",
        ...(config?.headers || {}),
      },
    } as any);
    return response as unknown as Blob;
  }

  async delete<T extends Object>(
    path: string,
    data: Map<string, any> | any = {}
  ): Promise<BaseApiResponseModel<T>> {
    let response = await api.delete(path, {
      params: data,
    });
    return ModelConverter.decode(response, BaseApiResponseModel<T>);
  }

  async put<T extends Object>(
    path: string,
    data: Map<string, any> | any = {},
    config?: Map<string, any> | any
  ): Promise<BaseApiResponseModel<T>> {
    let response = await api.put(path, data, config);
    return ModelConverter.decode(response, BaseApiResponseModel<T>);
  }
}

export default new AxiosClient();
